import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useConfirmationModal, useModals, useSnackbars } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import plur from 'plur';
import { useAnalytics } from '@/services/analytics';
import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import type { IdentifiersConfig } from '@/features/people';
import type { AttributeScope } from '@/services/db/data-index/entity';
import {
  useBatchUpdateDataIndexAttributeMetadata,
  useBatchUpdateDataIndexEventMetadata,
  useDeleteDataIndexAttributes,
} from '@/services/db/data-index/mutations';
import { getDataIndexAttributeUsageQueryOptions } from '@/services/db/data-index/queries';
import { bulkDeleteBlockReason } from './utils/bulk-delete';
import { usageCountsLookup } from './utils/usage';
import { descriptionError } from './utils/editing';
import {
  DataIndexLocation,
  FALLBACK_OBJECT_TYPE_ID,
  type DataIndexAnalyticsType,
  type DataIndexItemKind,
} from './utils/types';
import { dataIndexBulkActions, type BulkActionKey } from './components/bulk-actions';
import { GenerateDescriptionsModal } from './components/generate-descriptions-modal';
import { DeleteMessage } from './components/delete-message';

/** One row's pending description and tags while several are edited together. */
type BulkEditDraft = {
  descriptions: Record<string, string>;
  tagIds: Record<string, number[]>;
};

/** What a row already has, as the bulk edit starts from it. */
export type BulkEditSeed = {
  id: string;
  description: string | null;
  tagIds: readonly number[];
};

/**
 * Editing several rows at once.
 *
 * The pending values live here rather than in each cell, because one Save
 * writes them all in a single request. Which rows are being edited is the
 * list of names: everything else on the page reads that to know it is in a
 * bulk edit at all.
 */
export function useBulkEdit() {
  const [names, setNames] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<BulkEditDraft>({ descriptions: {}, tagIds: {} });
  // How many cells are naming a tag. One Save covers every row, so it has to
  // wait for a name the way each cell's own Save does: saving in between
  // would write the set without the new tag and end the edit, leaving the tag
  // created and attached to nothing.
  const [namingCount, setNamingCount] = useState(0);
  // Which edit is the live one. A draft update carries the session it was
  // started in, so work that answers after a Cancel cannot write into the
  // edit that replaced it — an unmount guard is not enough, because the cell
  // it belonged to may still be on screen.
  const session = useRef(0);

  return {
    names,
    session: session.current,
    isEditing: names != null,
    isNaming: namingCount > 0,
    onNaming: (isNaming: boolean) => setNamingCount((current) => Math.max(0, current + (isNaming ? 1 : -1))),
    descriptionOf: (name: string) => draft.descriptions[name] ?? '',
    tagIdsOf: (name: string) => draft.tagIds[name] ?? [],
    setDescription: (name: string, description: string, forSession: number) => {
      if (forSession !== session.current) {
        return;
      }
      setDraft((current) => ({ ...current, descriptions: { ...current.descriptions, [name]: description } }));
    },
    /**
     * Takes an updater rather than a set, so a chip removed while a tag is
     * being named is not written back by the create that lands after it.
     */
    setTagIds: (name: string, next: (chosen: number[]) => number[], forSession: number) => {
      if (forSession !== session.current) {
        return;
      }
      setDraft((current) => ({
        ...current,
        tagIds: { ...current.tagIds, [name]: next(current.tagIds[name] ?? []) },
      }));
    },
    /** Begin, seeded with what each row already has, so a save changes nothing by itself. */
    start: (rows: readonly BulkEditSeed[]) => {
      session.current += 1;
      setDraft({
        descriptions: Object.fromEntries(rows.map((row) => [row.id, row.description ?? ''])),
        tagIds: Object.fromEntries(rows.map((row) => [row.id, [...row.tagIds]])),
      });
      setNames(rows.map((row) => row.id));
    },
    /** What one save should send. */
    updates: () =>
      (names ?? []).map((name) => ({
        name,
        description: draft.descriptions[name] ?? '',
        tag_ids: draft.tagIds[name] ?? [],
      })),
    stop: () => {
      session.current += 1;
      setNames(null);
      setDraft({ descriptions: {}, tagIds: {} });
      setNamingCount(0);
    },
  };
}

/**
 * Why a selection cannot be edited together, or nothing when it can.
 *
 * Editing several rows at once shows only those rows, so it can only cover
 * the ones this page has: a selection survives paging, and starting an edit
 * over rows that are not here would drop them silently — or, if none of them
 * are here, open an edit over nothing that only Cancel escapes. The page this
 * replaces writes only the rows its table still holds and says nothing about
 * the rest (`ui/app/components/data-index-list-table/index.gts:1010-1016`).
 *
 * @internal exported for its test
 */
export function bulkEditBlockReason(
  selected: readonly string[],
  onPage: readonly { id: string }[],
): string | undefined {
  return onPage.length === selected.length ? undefined : 'Only rows on this page can be edited together.';
}

/**
 * How many things use each selected attribute, which is what decides whether
 * it can be deleted. Only asked for an attribute selection: an event cannot
 * be deleted, so nothing about it turns on the answer.
 */
function useSelectionUsage(workspaceId: number, kind: DataIndexItemKind, scope: AttributeScope, names: string[]) {
  // Only a person's attributes can be deleted, so only they need the answer.
  const enabled = kind !== 'event' && scope.kind === 'profile' && names.length > 0;

  return useQuery({
    ...getDataIndexAttributeUsageQueryOptions(workspaceId, scope, names),
    enabled,
  });
}

/**
 * What one save writes for a row.
 *
 * `tag_ids` is omitted rather than empty when tags are not being changed:
 * the endpoint reads an absent field as "leave them" and an empty list as
 * "remove them all".
 */
type BulkUpdate = { name: string; description: string; tag_ids?: number[] };

/** What every write on this page shares: which family it is, and its noun. */
type BulkFamily = {
  workspaceId: number;
  kind: DataIndexItemKind;
  scope: AttributeScope;
};

function nounFor(kind: DataIndexItemKind): string {
  return kind === 'event' ? 'event' : 'attribute';
}

/**
 * What a batch says it changed. A bulk edit names the rows; applying generated
 * descriptions names what was written to them, as the page this replaces does
 * (`data-index-list-table/index.gts:865-886`).
 */
type BulkSaveSubject = 'rows' | 'descriptions';

function bulkSaveSubject(subject: BulkSaveSubject, noun: string, count: number): string {
  return subject === 'descriptions' ? `descriptions for ${plur(noun, count)}` : plur(noun, count);
}

/**
 * Writing several rows in one request, and saying what became of it.
 *
 * The four batch endpoints differ only in their path and in what they call
 * the list of updates; the scope picks between them.
 */
function useBulkSave({ workspaceId, kind, scope }: BulkFamily) {
  const snackbars = useSnackbars();
  const [isSaving, setIsSaving] = useState(false);
  const batchAttributes = useBatchUpdateDataIndexAttributeMetadata({ workspaceId });
  const batchEvents = useBatchUpdateDataIndexEventMetadata({ workspaceId });
  const noun = nounFor(kind);

  const save = async (
    updates: BulkUpdate[],
    subject: BulkSaveSubject = 'rows',
    isCancelled: () => boolean = () => false,
  ): Promise<boolean> => {
    // Nothing to write is not a write. The page this replaces returns here
    // too, rather than sending an empty list and reporting a success.
    if (updates.length === 0) {
      return false;
    }
    // The box shows the error; the save has to honour it, or the whole
    // batch comes back refused with nothing to point at.
    const invalid = updates.find((update) => descriptionError(update.description) != null);
    if (invalid != null) {
      snackbars.create({
        id: 'data-index-bulk-save',
        variant: 'critical',
        message: `"${invalid.name}" has a description that is too long.`,
      });
      return false;
    }

    setIsSaving(true);
    try {
      // The answer carries `updated` and `tags_updated`, and neither is read:
      // a response reporting fewer rows than were sent still reads as a whole
      // success here. That is what the page this replaces does, and holding to
      // it is deliberate — see the PR, which records it for an owner to decide
      // rather than diverging here.
      await (kind === 'event'
        ? batchEvents.mutateAsync({ events: updates })
        : batchAttributes.mutateAsync({ scope, attributes: updates }));
    } catch {
      // A cancelled run reports nothing of its own (`generate-descriptions-modal/index.gts:111-119`).
      if (!isCancelled()) {
        snackbars.create({
          id: 'data-index-bulk-save',
          variant: 'critical',
          message: `Failed to update ${bulkSaveSubject(subject, noun, updates.length)}`,
        });
      }
      return false;
    } finally {
      setIsSaving(false);
    }

    if (isCancelled()) {
      return false;
    }

    snackbars.create({
      id: 'data-index-bulk-save',
      variant: 'success',
      message: `Successfully updated ${bulkSaveSubject(subject, noun, updates.length)}`,
    });
    return true;
  };

  return { save, isSaving };
}

/**
 * Deleting a selection, once somebody has typed how many they mean.
 *
 * The work happens in the background, so a success here means the request
 * was accepted rather than that the attributes are gone — which is why the
 * rows are still listed afterwards.
 */
function useBulkDelete(
  { workspaceId, kind }: BulkFamily,
  names: readonly string[],
  onDeleted: (names: readonly string[]) => void,
  onDone: () => void,
) {
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const deleteAttributes = useDeleteDataIndexAttributes({ workspaceId });
  const noun = nounFor(kind);

  const remove = async () => {
    const confirmed = await confirm({
      title: `Delete ${names.length} ${plur(noun, names.length)}`,
      message: <DeleteMessage workspaceId={workspaceId} names={names} />,
      confirmLabel: 'Delete',
      isCritical: true,
      // Typing the count is what makes this a decision rather than a click:
      // deleting an attribute cannot be undone.
      confirmationPhrase: String(names.length),
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteAttributes.mutateAsync({ names: [...names] });
    } catch {
      snackbars.create({
        id: 'data-index-bulk-delete',
        variant: 'critical',
        message: `Failed to delete ${plur(noun, names.length)}`,
      });
      return;
    }
    snackbars.create({ id: 'data-index-bulk-delete', message: `Requested ${plur(noun, names.length)} for deletion` });
    onDeleted(names);
    onDone();
  };

  return remove;
}

/**
 * Asking the model to describe a selection.
 *
 * What comes back is saved rather than offered for review: fifty
 * descriptions have nowhere to be reviewed, and the row editors are how
 * anyone changes one afterwards.
 */
function useGenerateDescriptions(
  family: BulkFamily,
  names: readonly string[],
  save: (updates: BulkUpdate[], subject?: BulkSaveSubject, isCancelled?: () => boolean) => Promise<boolean>,
  onDone: () => void,
) {
  const modals = useModals();
  const { kind, scope, workspaceId } = family;

  return () => {
    modals.open(
      GenerateDescriptionsModal,
      {
        workspaceId,
        type: kind === 'event' ? 'event' : 'attribute',
        noun: nounFor(kind),
        names,
        objectTypeId: scope.kind === 'profile' ? undefined : scope.objectTypeId,
        onGenerated: async (descriptions: Record<string, string>, isCancelled: () => boolean) => {
          // Only the description is being written, so the tags are not named
          // at all and the endpoint leaves them as they are.
          const updates = Object.entries(descriptions).map(([name, description]) => ({ name, description }));
          // Cancelled mid-save, the write still lands but the selection stays,
          // as Ember's cancelled task never reaches its reset.
          const saved = await save(updates, 'descriptions', isCancelled);
          if (saved) {
            onDone();
          }
          return saved;
        },
      },
      { shouldCloseOnUnmount: true },
    );
  };
}

/** The reasons a delete is refused, in the workspace's own words. */
function useDeleteBlockReason(
  family: BulkFamily,
  names: string[],
  canEdit: boolean,
  identifiers: IdentifiersConfig | null | undefined,
) {
  const usage = useSelectionUsage(family.workspaceId, family.kind, family.scope, names);

  return bulkDeleteBlockReason({
    names,
    objectTypeId: family.scope.kind === 'profile' ? FALLBACK_OBJECT_TYPE_ID : family.scope.objectTypeId,
    canEdit,
    isUsageLoading: usage.isPending && names.length > 0,
    isUsageError: usage.isError,
    countsFor: (name) => usageCountsLookup(usage.data?.[name]),
    identifiers,
    terms: {
      campaignIndefinite: 'an automation',
      campaignsPlural: 'automations',
      newsletterIndefinite: 'a one-time send',
      newslettersPlural: 'one-time sends',
    },
  });
}

export type DataIndexBulkActionsOptions = {
  workspaceId: number;
  kind: DataIndexItemKind;
  scope: AttributeScope;
  /** Records rows the backend has been asked to delete, so the list can hide them. */
  onDeleted: (names: readonly string[]) => void;
  rows: readonly BulkEditSeed[];
  identifiers: IdentifiersConfig | null | undefined;
  selection: Record<string, boolean>;
  onSelectionChange: (selection: Record<string, boolean>) => void;
};

/**
 * Everything that acts on a selection: editing several rows at once, asking
 * the model to describe them, and deleting them.
 *
 * One hook because the three share a selection and all three clear it when
 * they finish — a selection that outlived the rows it named would be acted
 * on again by the next action.
 */
export function useDataIndexBulkActions(options: DataIndexBulkActionsOptions) {
  const { kind, scope, rows, identifiers, selection, onSelectionChange } = options;
  const family: BulkFamily = { workspaceId: options.workspaceId, kind, scope };

  const analytics = useAnalytics();
  const { hasPermission } = usePermissions();
  const bulkEdit = useBulkEdit();

  const canEdit = hasPermission('journeys/attribute_metadata/edit');
  // The single-row button sits inside AiEnabledGuard; a dropdown item is
  // config rather than JSX, so it reads the same setting directly.
  const isAiEnabled = useAccountSetting(AccountSetting.AiEnabled);
  const isEvent = kind === 'event';
  const type: DataIndexAnalyticsType = isEvent ? 'event' : 'attribute';
  const selected = useMemo(() => Object.keys(selection).filter((name) => selection[name] === true), [selection]);

  const clearSelection = () => onSelectionChange({});
  const onPage = useMemo(() => rows.filter((row) => selected.includes(row.id)), [rows, selected]);
  const editBlockReason = bulkEditBlockReason(selected, onPage);
  const { save, isSaving } = useBulkSave(family);
  const remove = useBulkDelete(family, selected, options.onDeleted, clearSelection);
  const askTheModel = useGenerateDescriptions(family, selected, save, clearSelection);
  const deleteBlockReason = useDeleteBlockReason(family, selected, canEdit, identifiers);

  const onAction = (action: BulkActionKey) => {
    void analytics.track('dataIndex', 'bulkActionClicked', {
      action,
      item_count: selected.length,
      type,
      location: DataIndexLocation.Table,
    });

    if (action === 'edit') {
      if (editBlockReason != null) {
        return;
      }
      bulkEdit.start(onPage);
    } else if (action === 'generate_descriptions') {
      askTheModel();
    } else {
      void remove();
    }
  };

  const names = bulkEdit.names;

  return {
    // The table only learns about a bulk edit while one is happening.
    bulkEdit:
      names == null
        ? null
        : {
            names,
            session: bulkEdit.session,
            descriptionOf: bulkEdit.descriptionOf,
            tagIdsOf: bulkEdit.tagIdsOf,
            setDescription: bulkEdit.setDescription,
            setTagIds: bulkEdit.setTagIds,
            onNaming: bulkEdit.onNaming,
          },
    actions: dataIndexBulkActions({
      withGenerateDescriptions: isAiEnabled,
      canEdit,
      selectedCount: selected.length,
      editBlockReason,
      // Only a person's attributes can be deleted through this list.
      withDelete: !isEvent && scope.kind === 'profile',
      deleteBlockReason,
      onAction,
    }),
    bar:
      names == null
        ? null
        : {
            count: names.length,
            noun: nounFor(kind),
            isSaving,
            // A tag is still being named somewhere in the edit, and saving
            // now would write the set without it.
            isNaming: bulkEdit.isNaming,
            onSave: () => {
              void (async () => {
                if (bulkEdit.isNaming) {
                  return;
                }
                if (await save(bulkEdit.updates())) {
                  bulkEdit.stop();
                  clearSelection();
                }
              })();
            },
            onCancel: () => {
              bulkEdit.stop();
              clearSelection();
            },
          },
  };
}
